import { Kbd } from '@heidi/design-system/kbd';
import { cn } from '@heidi/utils';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Key } from 'ts-key-enum';

/**
 * "Hot tip" advertising the global Cmd/Ctrl+Shift+H shortcut that
 * summons the floating Heidi overlay from anywhere.
 *
 * BOTH GATES ARE THE HOST'S. The shortcut is registered by the Tauri app and
 * hidden behind a feature flag, so whether the tip is meaningful is an
 * `isTauri() && useIsCmdShiftHOverlayEnabled()` question — a flag resolver and
 * a platform probe, neither of which a package may hold. The same is true of
 * the modifier glyph: reading the OS family is the app's job. What stays here
 * is the mount guard, because that one is about THIS component's own
 * hydration: `enabled` is false during SSR and true on the client, so the
 * first client render must still match the server's and only the post-mount
 * render may show the tip.
 */
export interface WorkShortcutTipProps {
  className?: string;
  /** Host-resolved: desktop AND the overlay shortcut flag on. */
  enabled: boolean;
  /** Host-resolved: which modifier glyph this OS shows. */
  isMac: boolean;
}

export function WorkShortcutTip({
  className,
  enabled,
  isMac,
}: WorkShortcutTipProps): JSX.Element | null {
  const intl = useIntl();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  if (!mounted || !enabled) return null;

  const shortcutAriaLabel = intl.formatMessage(
    {
      defaultMessage: '{modifier, select, command {Command} other {Control}} + Shift + H',
      id: 'FALhJk7/dj',
      description: 'Accessible name for the global Heidi keyboard shortcut',
    },
    { modifier: isMac ? 'command' : 'control' }
  );

  return (
    <div
      data-testid="work-shortcut-tip"
      className={cn(
        'flex items-center justify-center gap-2 text-[12px] text-text-tertiary',
        'duration-500 animate-in fade-in',
        className
      )}
    >
      <FormattedMessage
        defaultMessage="<copy>Tip: summon Heidi from anywhere with</copy><shortcutKeys>this shortcut</shortcutKeys>"
        id="sY8gv2Tbtx"
        description="Hint on the Work home advertising the global overlay shortcut"
        values={{
          copy: (chunks) => <span>{chunks}</span>,
          shortcutKeys: () => (
            <span
              className="flex items-center gap-1"
              role="img"
              aria-label={shortcutAriaLabel}
            >
              <Kbd size="compact" keyType="modifier" isMac={isMac} className="bg-surface">
                {Key.Meta}
              </Kbd>
              <Kbd size="compact" keyType="modifier" isMac={isMac} className="bg-surface">
                {Key.Shift}
              </Kbd>
              <Kbd size="compact" className="bg-surface">
                H
              </Kbd>
            </span>
          ),
        }}
      />
    </div>
  );
}
